import { LoadingStageModel, ValueModel } from '@kts-front/models';
import { Nullable } from '@kts-front/types';
import dayjs from 'dayjs';
import { action, computed, makeObservable, runInAction } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import { MeetingEntity, MeetingEntityApi, meetingSchema } from 'entities/Meeting';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { NotificationsStore } from 'stores/global/RootStore/NotificationsStore';
import { PopupStore } from 'stores/local/shared/PopupStore';
import { isCancelledRequest } from 'utils/isCancelledRequest';
import { logZodError } from 'utils/logZodError';

type Params = { notificationStore: NotificationsStore };

export class MeetingDrawerStore extends PopupStore<number> {
  private readonly _notificationStore: NotificationsStore;
  readonly meeting = new ValueModel<Nullable<MeetingEntity>>(null);

  readonly loadingStage = new LoadingStageModel();

  constructor({ notificationStore }: Params) {
    super();

    this._notificationStore = notificationStore;

    makeObservable(this, {
      canEditMeeting: computed,
      canCancelMeeting: computed,
      cancelMeeting: action.bound,
      fetch: action.bound,
    });
  }

  get canEditMeeting(): boolean {
    return Boolean(this.meeting.value && this.meeting.value.status === 'scheduled');
  }

  get canCancelMeeting(): boolean {
    return Boolean(
      this.canEditMeeting && this.meeting.value && dayjs(this.meeting.value.slot.startDate).isAfter(dayjs()),
    );
  }

  open(id: number): void {
    super.open(id);
    this.fetch(id);
  }

  close(): void {
    super.close();
    this.meeting.reset();
    this.loadingStage.reset();
  }

  async fetch(id: number): Promise<void> {
    if (!this.entity) {
      return;
    }

    this.loadingStage.loading();

    const request = apiStore.createRequest({ url: ENDPOINTS.meeting.get(id) });

    const response = await request.call<MeetingEntityApi>();

    if (response.isError) {
      this.loadingStage.error();

      logApiErrWithSentry({
        endpoint: ENDPOINTS.meeting.get(id),
        method: 'GET',
        requestPayload: { meeting_id: id },
        response,
      });

      return;
    }

    const parsed = meetingSchema.safeParse(response.data);

    if (parsed.error) {
      this.loadingStage.error();

      this._notificationStore.showError({ message: parsed.error.message });

      logZodError(parsed.error);

      return;
    }

    runInAction(() => {
      this.meeting.change(parsed.data);
      this.loadingStage.success();
    });
  }

  async cancelMeeting() {
    if (!this.meeting.value || this.loadingStage.isLoading) {
      return;
    }

    this.loadingStage.loading();
    const response = await apiStore
      .createRequest({ url: ENDPOINTS.meeting.cancel(this.meeting.value.id), method: 'POST' })
      .call();

    runInAction(() => {
      if (response.isError) {
        if (isCancelledRequest(response)) {
          this.loadingStage.reset();

          return;
        }

        logApiErrWithSentry({
          endpoint: this.meeting.value
            ? ENDPOINTS.meeting.cancel(this.meeting.value?.id)
            : 'Не удалось отменить встречу',
          method: 'POST',
          requestPayload: {},
          response,
        });

        this.loadingStage.error();
        this._notificationStore.showErrorByResponse(response);

        return;
      }
      this.loadingStage.success();
      this._notificationStore.showSuccess('Встреча отменена');
      this.close();
    });
  }
}
